import gql from 'graphql-tag';

import { ActivityReactionWithCommentReactionsFragment } from '@/shared/Reactions';

export const PrimaryGalleryImageFragment = gql`
  fragment PrimaryGalleryImageFragment on ResultSubmissionImage {
    __typename
    id
    uri
    reactions {
      ...ActivityReactionWithCommentReactionsFragment
    }
  }
  ${ActivityReactionWithCommentReactionsFragment}
`;

const ResultSubmissionBaseFragment = gql`
  fragment ResultSubmissionBaseFragment on ResultSubmission {
    __typename
    id
    createdAt
    participantIndex
    givenName
    familyName
    eventId
    raceId
    legId
    submissionIndex
    primaryIdentityId
    finishingTime
    enteredRaceStats {
      chosenUnit
      totalMetricLength
      totalTime
    }
    metrics {
      time
      distance
      elevation
      speed
      count
    }
  }
`;

export const RESULT_SUBMISSION_FRAGMENT = 'ResultSubmissionFragment';
export const ResultSubmissionFragment = gql`
  fragment ${RESULT_SUBMISSION_FRAGMENT} on ResultSubmission {
    gender
    age
    activityUrl
    raceTimeVerificationStatus
    galleryImages {
      primary {
        ...PrimaryGalleryImageFragment
      }
    }
    verificationImages {
      primary {
        id
        uri
      }
    }
    group {
      id
      name
    }
    ...ResultSubmissionBaseFragment
  }
  ${PrimaryGalleryImageFragment}
  ${ResultSubmissionBaseFragment}
`;

const FullResultSubmissionFragment = gql`
  fragment FullResultSubmissionFragment on ResultSubmission {
    gender
    age
    activityUrl
    raceTimeVerificationStatus
    galleryImages {
      primary {
        ...PrimaryGalleryImageFragment
      }
      all {
        id
        uri
      }
    }
    verificationImages {
      primary {
        id
        uri
      }
      all {
        id
        uri
      }
    }
    group {
      id
      name
    }
    ...ResultSubmissionBaseFragment
  }
  ${PrimaryGalleryImageFragment}
  ${ResultSubmissionBaseFragment}
`;

const UserResultSubmissionFragment = gql`
  fragment UserResultSubmissionFragment on ResultSubmission {
    bookingId
    bookingReference
    ...ResultSubmissionBaseFragment
  }
  ${ResultSubmissionBaseFragment}
`;

export const ResultSubmissionsQuery = gql`
  query ResultSubmissions(
    $eventId: Int
    $limit: Int
    $offset: Int
    $filters: ResultSubmissionsFiltersInput
    $orderBy: ResultSubmissionOrderBy
  ) {
    resultSubmissions: resultSubmissions(
      input: {
        eventId: $eventId
        limit: $limit
        offset: $offset
        filters: $filters
        orderBy: $orderBy
      }
    ) {
      totalCount
      edges {
        node {
          ...ResultSubmissionFragment
        }
      }
    }
  }
  ${ResultSubmissionFragment}
`;

// Old non LDT booking flow, hopefully can deprecate after new bib collection stuff
export const CreateResultSubmissionMutation = gql`
  mutation CreateResultSubmission($input: ResultSubmissionInput!) {
    createResultSubmission(input: $input)
  }
`;

export const SubmitResultFromBookingMutation = gql`
  mutation SubmitResultFromBooking($input: ResultSubmissionParticipantInput!) {
    submitResultFromBooking(input: $input) {
      id
      submissionIndex
    }
  }
`;

export const GetResultSubmissionById = gql`
  query GetResultSubmissionByIdQuery($id: ID!) {
    resultSubmission(id: $id) {
      ...FullResultSubmissionFragment
    }
  }
  ${FullResultSubmissionFragment}
`;

export const UpdateResultSubmissionMutation = gql`
  mutation UpdateResultSubmission(
    $id: ID!
    $bookingId: ID
    $input: UpdateResultSubmissionInput!
  ) {
    updateResultSubmission(id: $id, bookingId: $bookingId, input: $input) {
      id
      submissionIndex
    }
  }
`;

export const RemoveResultSubmissionMutation = gql`
  mutation RemoveResultSubmission($id: ID!, $bookingId: ID) {
    removeResultSubmission(id: $id, bookingId: $bookingId)
  }
`;

export const GetResultSubmissionTotalCount = gql`
  query GetResultSubmissionTotalCount($eventIds: [Int]!) {
    resultSubmissionTotalCount(input: { eventIds: $eventIds }) {
      eventCounts {
        eventId
        count
      }
      totalCount
    }
  }
`;

export const UserRaceEntriesAndResultSubmissionsQuery = gql`
  query UserRaceEntriesAndResultSubmissionsQuery(
    $primaryIdentityId: String!
    $eventId: ID
    $raceId: ID
  ) {
    userByPrimaryIdentityId(primaryIdentityId: $primaryIdentityId) {
      id
      raceEntries(filters: { eventId: $eventId, raceId: $raceId }) {
        id
        status
        bookingId
        numberOfParticipants
        eventSummary {
          eventId
          title
          raceId
          raceStartDate
          raceTitle
          raceType
          submissionWindow {
            startDate
            endDate
          }
        }
        resultSubmissions {
          ...UserResultSubmissionFragment
        }
      }
      resultSubmissions(
        filters: {
          eventId: $eventId
          raceId: $raceId
          showIndividualSubmissions: false
        }
      ) {
        ...UserResultSubmissionFragment
      }
    }
  }
  ${UserResultSubmissionFragment}
`;

export const UserResultSubmissionsQuery = gql`
  query UserResultSubmissionsQuery(
    $primaryIdentityId: String!
    $eventId: ID
    $showIndividualSubmissions: Boolean
    $offset: Int
    $limit: Int
  ) {
    userByPrimaryIdentityId(primaryIdentityId: $primaryIdentityId) {
      id
      resultSubmissions(
        offset: $offset
        limit: $limit
        filters: {
          eventId: $eventId
          showIndividualSubmissions: $showIndividualSubmissions
        }
      ) {
        ...ResultSubmissionFragment
      }
    }
  }
  ${ResultSubmissionFragment}
`;

export const ResultSubmissionsAndRaceEntryByBooking = gql`
  query ResultSubmissionsAndRaceEntryByBooking(
    $bookingId: ID!
    $eventId: ID
    $raceId: ID
    $participantIndex: Int
    $submissionIndex: Int
    $intEventId: Int
  ) {
    resultSubmissions: resultSubmissionsByBooking(
      bookingId: $bookingId
      eventId: $eventId
      raceId: $raceId
      participantIndex: $participantIndex
      submissionIndex: $submissionIndex
    ) {
      ...FullResultSubmissionFragment
    }
    raceEntryByBookingId(bookingId: $bookingId, eventId: $intEventId) {
      id
      groupId
      eventSummary {
        raceId
        eventId
      }
      participants {
        firstName
        lastName
      }
    }
  }
  ${FullResultSubmissionFragment}
`;
